import { CalendarIcon, EllipseFilledIcon, EllipseHalfIcon, LocationIcon, WorldIcon } from '@bi-digital/icons';
import { Fragment, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { Campus, Load, TeachingLanguage, Term } from '../../../types';
import styles from './ProductInfo.module.css';

export type ProductInfoProps = {
    loads: Load[];
    terms: Term[];
    teachingLanguages: TeachingLanguage[];
    campuses: Campus[];
    action: ReactNode;
};

export const ProductInfo = ({ loads, terms, teachingLanguages, campuses, action }: ProductInfoProps) => {
    const { t } = useTranslation();

    const items: ListItemProps[] = [
        loads.length > 0 && {
            icon: loads.every((load) => load === 'FullTime') ? (
                <EllipseFilledIcon aria-hidden='true' />
            ) : (
                <EllipseHalfIcon aria-hidden='true' />
            ),
            label: t('cart:loadType'),
            value: loads.map((load) => t(`cart:load.${load}`)).join(', '),
        },
        terms.length > 0 && {
            icon: <CalendarIcon aria-hidden='true' />,
            label: t('cart:term'),
            value: terms.map((term) => t(`cart:${term.semester.toLowerCase()}`, { year: term.year })).join(', '),
        },
        teachingLanguages.length > 0 && {
            icon: <WorldIcon aria-hidden='true' />,
            label: t('cart:languageLabel'),
            value: teachingLanguages.map((language) => t(`cart:language.${language}`)).join(', '),
        },
        campuses.length > 0 && {
            icon: <LocationIcon aria-hidden='true' />,
            label: t('cart:location'),
            value: campuses.map((campus) => t(`cart:campus.${campus}`, { defaultValue: campus })).join(', '),
        },
    ].filter(Boolean) as ListItemProps[];

    return (
        <div className={styles.surface} data-color='primary'>
            <section className={styles.container} aria-label={t('cart:AriaLabelProgrammeInformation')}>
                <div className={styles.list}>
                    {items.map((item, index) => (
                        <Fragment key={index}>
                            <ListItem {...item} />
                            {index < items.length - 1 && <span className={styles.divider} />}
                        </Fragment>
                    ))}
                </div>
                {action}
            </section>
        </div>
    );
};

type ListItemProps = {
    icon: ReactNode;
    label: string;
    value: string;
};

const ListItem = ({ icon, label, value }: ListItemProps) => (
    <div className={styles.listItem}>
        {icon}
        <div>
            <p className={styles.label}>{label}</p>
            <p className={styles.value}>{value}</p>
        </div>
    </div>
);
